import { useState } from "react";
import type { StepDetailProps } from "./index";
import { STEP_OUTPUT_SCHEMAS, zodIssuesToErrors } from "./schemas";
import { StepActions } from "./StepActions";

export function QueryDetail({
  step,
  play,
  workspaceId,
  currentUserId,
  persona,
}: StepDetailProps) {
  const dataSource =
    typeof step.inputs["data_source"] === "string"
      ? (step.inputs["data_source"] as string)
      : "—";
  const queryText =
    typeof step.inputs["query_text"] === "string"
      ? (step.inputs["query_text"] as string)
      : "";
  const existingResult =
    step.output && typeof step.output === "object"
      ? (step.output as Record<string, unknown>)
      : null;

  const [resultRef, setResultRef] = useState<string>("");
  const [summary, setSummary] = useState<string>("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const buildOutput = (): Record<string, unknown> | null => {
    const payload: Record<string, unknown> = { result_ref: resultRef };
    if (summary) payload["summary"] = summary;
    const parsed = STEP_OUTPUT_SCHEMAS["query"].safeParse(payload);
    if (!parsed.success) {
      const map: Record<string, string> = {};
      for (const e of zodIssuesToErrors(parsed.error)) map[e.path] = e.message;
      setErrors(map);
      return null;
    }
    setErrors({});
    return parsed.data as Record<string, unknown>;
  };

  return (
    <div className="ho-detail ho-query" data-testid="step-detail-query">
      <div className="ho-eb">QUERY · DATA PULL</div>
      <h2 className="ho-h2">{step.title}</h2>
      <div className="ho-meta-row">
        <span className="pill">{dataSource}</span>
        {step.completedAt ? (
          <span style={{ color: "var(--text-3)" }}>
            · ran {new Date(step.completedAt).toLocaleString()}
          </span>
        ) : null}
      </div>
      <div className="ho-q-result" data-testid="query-text">
        <div className="ho-q-h">QUERY</div>
        <pre
          style={{
            background: "var(--surface-2)",
            padding: 10,
            borderRadius: 6,
            overflow: "auto",
            fontSize: 12,
          }}
        >
          {queryText}
        </pre>
        {existingResult ? (
          <>
            <div className="ho-q-h" style={{ marginTop: 12 }}>
              RESULT
            </div>
            <pre
              style={{
                background: "var(--surface-2)",
                padding: 10,
                borderRadius: 6,
                overflow: "auto",
                fontSize: 12,
              }}
              data-testid="query-result"
            >
              {JSON.stringify(existingResult, null, 2)}
            </pre>
          </>
        ) : null}
      </div>

      {step.state === "running" ? (
        <div style={{ display: "grid", gap: 8, marginTop: 12 }}>
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              RESULT REF
            </span>
            <input
              type="text"
              data-testid="query-result-ref"
              value={resultRef}
              onChange={(e) => setResultRef(e.target.value)}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
              }}
            />
          </label>
          {errors["result_ref"] ? (
            <span
              style={{ color: "var(--danger)", fontSize: 12 }}
              data-testid="query-error-result-ref"
            >
              {errors["result_ref"]}
            </span>
          ) : null}
          <label>
            <span
              style={{
                fontSize: 11.5,
                color: "var(--text-3)",
                letterSpacing: ".14em",
              }}
            >
              SUMMARY (optional)
            </span>
            <textarea
              data-testid="query-summary"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={3}
              style={{
                marginTop: 4,
                width: "100%",
                border: "1px solid var(--border)",
                borderRadius: 6,
                padding: 8,
                fontFamily: "inherit",
                fontSize: 13,
              }}
            />
          </label>
        </div>
      ) : null}

      <StepActions
        step={step}
        workspaceId={workspaceId}
        playId={play.id}
        currentUserId={currentUserId}
        persona={persona}
        buildOutput={buildOutput}
      />
    </div>
  );
}
